import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

import { Course, CourseSection, User, USERS, VueFire } from '@codee-club/common/dist/dao'

import Import from '../Import/Import.vue'

@Component({
  metaInfo(this: SectionList) {
    return {}
  },
  components: { Import }
})
export default class SectionList extends Vue {
  sectionIndex = 0
  students: Array<VueFire<User>> = []
  isImportModalActive = false

  async mounted(): Promise<void> {
    if (this.course !== undefined) {
      await this.bindStudents()
      await this.ensureSection()
    }
  }

  @Watch('course')
  async onCourseChanged(): Promise<void> {
    if (this.course !== undefined) {
      if (this.students.length === 0) await this.bindStudents()
      await this.ensureSection()
    }
  }

  async bindStudents(): Promise<void> {
    if (this.course !== undefined) {
      await this.$bind('students', this.$db.collection(USERS).where('courseIds', 'array-contains', this.course.id))
    }
  }

  async ensureSection(): Promise<void> {
    if (this.course !== undefined && (this.course.sections === undefined || Object.keys(this.course.sections).length === 0)) {
      await this.addSection('1')
    }
  }

  get course(): VueFire<Course> {
    return this.$store.getters.findCourseBySlug(this.$route.params.courseSlug)
  }

  get sectionsSorted(): Array<VueFire<CourseSection>> {
    if (this.course?.sections === undefined) return []

    const sections = Object.entries(this.course.sections).map(([id, value]) => ({ id, ...value })) as Array<VueFire<CourseSection>>
    return sections.sort((s1, s2) => s1.code.localeCompare(s2.code))
  }

  get section(): VueFire<CourseSection> {
    return this.sectionsSorted[this.sectionIndex]
  }

  get studentsForSection(): Array<VueFire<User>> {
    return this.sectionIndex === -1 ? this.students : this.students.filter((student) => this.section?.studentIds?.includes(student.id))
  }

  async addSection(code?: string): Promise<void> {
    const sectionCode = (typeof code === 'string')
      ? code
      : await new Promise<string | null>((resolve) => {
        this.$buefy.dialog.prompt({
          title: 'Add Section',
          message: 'Enter section code',
          inputAttrs: { placeholder: 'e.g. 1' },
          trapFocus: true, // eslint-disable-line woke/LGBTQ
          onConfirm: (value) => resolve(value),
          onCancel: () => resolve(null)
        })
      })

    if (sectionCode === null || sectionCode === undefined || sectionCode === '') return

    const sectionId = this.$context.getGuid()
    const sections = { ...(this.course.sections ?? {}), [sectionId]: { code: sectionCode } }
    await this.$dao.updateCourse(this.course.id, { sections })
  }

  async editSection(section: VueFire<CourseSection>): Promise<void> {
    this.$buefy.dialog.prompt({
      title: 'Edit Section',
      message: 'Edit section code',
      inputAttrs: { value: section.code },
      trapFocus: true, // eslint-disable-line woke/LGBTQ
      onConfirm: async (value) => {
        const sections = { ...(this.course.sections ?? {}), [section.id]: { ...section, code: value } }
        await this.$dao.updateCourse(this.course.id, { sections })
      }
    })
  }

  async deleteSection(sectionId: string): Promise<void> {
    if (this.sectionsSorted.length <= 1) return

    const section = this.course.sections?.[sectionId]
    if (section?.studentIds !== undefined && section.studentIds.length > 0) {
      this.$buefy.toast.open({
        message: 'Cannot delete section with students',
        type: 'is-warning'
      })
      return
    }

    this.$buefy.dialog.confirm({
      title: 'Delete Section',
      message: 'Are you sure you want to delete this section?',
      type: 'is-danger',
      hasIcon: true,
      onConfirm: async () => {
        await this.$dao.updateCourse(this.course.id, {
          sections: { [sectionId]: this.$context.FieldValue.delete() }
        })
        if (this.sectionIndex >= this.sectionsSorted.length - 1) {
          this.sectionIndex = Math.max(0, this.sectionsSorted.length - 2)
        }
      }
    })
  }
}
